/**
 * Postal address vocabulary for the admin's address editor.
 *
 * The lists are copied from reader-app/src/lib/address.ts (checkout and the
 * member account page) so staff pick from the same names members do; the
 * server rule they feed is aamcirc/checkout/signup_details.py — country and
 * US state are stored as codes.
 */

export const US_STATES: [string, string][] = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["AS", "American Samoa"], ["GU", "Guam"], ["MP", "Northern Mariana Islands"],
  ["PR", "Puerto Rico"], ["VI", "U.S. Virgin Islands"],
  ["AA", "Armed Forces Americas"], ["AE", "Armed Forces Europe"],
  ["AP", "Armed Forces Pacific"],
];

// Names come from Intl rather than a hand-maintained table, so the list
// can't drift. US/CA/GB/AU float to the top — between them they cover
// nearly every subscriber we have — and the rest is alphabetical.
const PINNED = ["US", "CA", "GB", "AU"];
const COUNTRY_CODES = `
AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS
BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE
EG ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW
GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR
KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MO MP MQ
MR MS MT MU MV MW MX MY MZ NA NC NE NG NI NL NO NP NR NZ OM PA PE PF PG PH PK PL
PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV
SX SY SZ TC TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI
VN VU WS YE ZA ZM ZW
`.trim().split(/\s+/);

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const named: [string, string][] = COUNTRY_CODES.map(
  (code) => [code, regionNames.of(code) ?? code] as [string, string],
);
export const COUNTRIES: [string, string][] = [
  ...PINNED.map((c) => named.find(([code]) => code === c)!).filter(Boolean),
  ...named
    .filter(([code]) => !PINNED.includes(code))
    .sort((a, b) => a[1].localeCompare(b[1])),
];

const COUNTRY_NAME = new Map(COUNTRIES)
const STATE_NAME = new Map(US_STATES)

/** A stored country as an ISO code, or '' when it can't be told.

    Site-sold rows hold the code; SimpleCirc rows hold an upper-case name
    ("UNITED STATES", "CANADA") — matched to the list by name. Its
    misspellings ("UKINGDOM", "NEW ZELND") are left blank for staff to
    pick rather than guessed. */
export function countryCode(raw: string | null | undefined): string {
  const value = (raw ?? '').trim().toUpperCase()
  if (!value) return ''
  if (COUNTRY_NAME.has(value)) return value
  const hit = COUNTRIES.find(([, name]) => name.toUpperCase() === value)
  return hit ? hit[0] : ''
}

/** A stored US state as its code (accepts "FL" or "FLORIDA"), or ''. */
export function stateCode(raw: string | null | undefined): string {
  const value = (raw ?? '').trim().toUpperCase()
  if (!value) return ''
  if (STATE_NAME.has(value)) return value
  const hit = US_STATES.find(([, name]) => name.toUpperCase() === value)
  return hit ? hit[0] : ''
}

/** The country as staff read it: the full name, else the stored text. */
export function countryLabel(raw: string | null | undefined): string | null {
  const code = countryCode(raw)
  return code ? COUNTRY_NAME.get(code)! : (raw ?? null)
}

/** The state as staff read it: the full US state name, else the stored text. */
export function stateLabel(
  raw: string | null | undefined, country: string | null | undefined,
): string | null {
  if (countryCode(country) !== 'US') return raw ?? null
  const code = stateCode(raw)
  return code ? STATE_NAME.get(code)! : (raw ?? null)
}
